/** Helpers for the Pagos tab: date filtering, per-recorder totals and the printable daily close */

import type { EventPayment } from '@/Services/eventPayments.service';
import { normalizePaymentMethod, type EventPaymentMethod } from '@/models/eventPaymentMethods';

/** 'YYYY-MM-DD' in the user's local time zone */
export function localDateStr(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Payments whose local payment date falls in [from, to] (either bound optional) */
export function paymentsInRange(payments: EventPayment[], from?: string, to?: string): EventPayment[] {
  return payments.filter((p) => {
    const day = localDateStr(p.paid_at);
    return (!from || day >= from) && (!to || day <= to);
  });
}

export interface PaymentSummary {
  total: number;
  count: number;
  byMethod: Map<EventPaymentMethod | 'none', { count: number; total: number }>;
  byRecorder: { name: string; count: number; total: number }[];
}

/** Totals of the RECORDED payments (reversals are excluded) */
export function summarizePayments(payments: EventPayment[]): PaymentSummary {
  const recorded = payments.filter((p) => p.status === 'RECORDED');
  const byMethod: PaymentSummary['byMethod'] = new Map();
  const recorders = new Map<string, { name: string; count: number; total: number }>();

  for (const p of recorded) {
    const amount = Number(p.amount);
    const method = normalizePaymentMethod(p.method) ?? 'none';
    const m = byMethod.get(method) ?? { count: 0, total: 0 };
    byMethod.set(method, { count: m.count + 1, total: m.total + amount });

    const name = p.recorder?.full_name ?? 'Sin registrar';
    const r = recorders.get(name) ?? { name, count: 0, total: 0 };
    recorders.set(name, { name, count: r.count + 1, total: r.total + amount });
  }

  return {
    total: recorded.reduce((sum, p) => sum + Number(p.amount), 0),
    count: recorded.length,
    byMethod,
    byRecorder: [...recorders.values()].sort((a, b) => b.total - a.total),
  };
}

/**
 * Splits table rows over letter pages. The first page also carries the summary, and the last
 * one needs room for the signature block; if it doesn't fit, the signatures get their own page.
 */
export function paginateRows<T>(
  rows: T[],
  caps = { first: 10, other: 24, signatureRows: 5 },
): { pages: T[][]; signatureOnOwnPage: boolean } {
  const pages: T[][] = [];
  let i = 0;
  while (i < rows.length || pages.length === 0) {
    const cap = pages.length === 0 ? caps.first : caps.other;
    pages.push(rows.slice(i, i + cap));
    i += cap;
  }
  const lastIsFirst = pages.length === 1;
  const lastCap = lastIsFirst ? caps.first : caps.other;
  const signatureOnOwnPage = pages[pages.length - 1].length > lastCap - caps.signatureRows;
  return { pages, signatureOnOwnPage };
}
